import { useApolloClient } from '@apollo/client';
import { AgentComplianceReviewDocumentDocument } from '@client/graphql/__generated__/main-operations';
import {
  AgentComplianceReviewDocumentQuery,
  AgentComplianceReviewDocumentQueryVariables,
} from '@client/graphql/__generated__/types';
import { useEffect, useState } from 'react';
import {
  ComplianceDocumentSummary,
  CompliancePlatformRequirementIssue,
  ComplianceSuggestion,
} from '../components/types';
import { deriveAgentDocumentValidation } from './useAgentDocumentValidation';

/**
 * The per-document data the checklist-level resubmit needs for one tab: its
 * derived (non-USER-hidden) suggestions, its actionable checklist issues, and
 * the validation id required to key per-document suggestion ignores.
 */
export interface ChecklistItemDocDetail {
  ingestionDocumentId: string;
  documentName: string;
  ingestionDocumentValidationId: string | null;
  suggestions: ComplianceSuggestion[];
  platformRequirementIssues: CompliancePlatformRequirementIssue[];
  /**
   * How many contract documents this PDF holds, so the resubmit note only names
   * the owning form when the file actually has more than one.
   */
  contractDocumentCount: number;
}

interface UseChecklistItemDocumentsArgs {
  token: string | undefined;
  ingestionFolderUuid: string | undefined;
  /** The documents of the active checklist item (the tabs). */
  docs: ComplianceDocumentSummary[];
  /**
   * Bump to re-read every tab's detail. These details are fetched imperatively
   * into React state, so unlike a `useQuery` they do NOT react to cache writes —
   * an inline ignore would otherwise leave the resubmit note composed from a
   * pre-ignore snapshot (missing the agent's reason). Re-reading is cheap: the
   * query is `cache-first` and the ignore already patched the cache.
   */
  refreshKey?: number;
}

interface UseChecklistItemDocumentsResult {
  /** Per-tab detail in `docs` order; empty until loaded. */
  docDetails: ChecklistItemDocDetail[];
  /** True until every tab's detail has resolved. */
  loading: boolean;
}

/**
 * Loads the detail for EVERY document in the active checklist item so the
 * checklist-level "Ignore and resubmit" can (a) prepopulate the note with all
 * tabs' suggestions grouped by file name and (b) ignore the suggestions on the
 * non-active tabs (each keyed to its own validation id).
 *
 * Uses `cache-first` per document — the active tab is already cached by
 * {@link useAgentComplianceReviewDocument}, so this only fetches the siblings,
 * and revisiting an item is instant. Derives each tab's suggestions with the
 * shared {@link deriveAgentDocumentValidation} (no forked logic).
 */
export const useChecklistItemDocuments = ({
  token,
  ingestionFolderUuid,
  docs,
  refreshKey = 0,
}: UseChecklistItemDocumentsArgs): UseChecklistItemDocumentsResult => {
  const client = useApolloClient();
  const [docDetails, setDocDetails] = useState<ChecklistItemDocDetail[]>([]);
  const [loading, setLoading] = useState(false);

  // Re-run only when the set of tab ids changes (not on every render / array
  // identity change). Ordering is preserved via this key.
  const docIdsKey = docs.map((doc) => doc.ingestionDocumentId).join(',');

  useEffect(() => {
    if (token == null || ingestionFolderUuid == null || docs.length === 0) {
      setDocDetails([]);
      // Reset loading too — otherwise a guard hit after a started load leaves
      // `loading` stuck true (and `isResubmitReady` stuck false).
      setLoading(false);

      return;
    }

    let cancelled = false;
    setLoading(true);

    void Promise.all(
      docs.map(async (doc): Promise<ChecklistItemDocDetail> => {
        try {
          const { data } = await client.query<
            AgentComplianceReviewDocumentQuery,
            AgentComplianceReviewDocumentQueryVariables
          >({
            query: AgentComplianceReviewDocumentDocument,
            variables: {
              input: {
                token,
                ingestionFolderUuid,
                ingestionDocumentId: doc.ingestionDocumentId,
              },
            },
            fetchPolicy: 'cache-first',
            context: { skipGlobalErrorHandler: true },
          });

          const detail = data?.agentComplianceReviewDocument;
          const validation = deriveAgentDocumentValidation(detail);

          return {
            ingestionDocumentId: doc.ingestionDocumentId,
            documentName: detail?.documentName ?? doc.documentName,
            ingestionDocumentValidationId:
              detail?.ingestionDocumentValidationId ?? null,
            suggestions: validation.suggestions,
            platformRequirementIssues: detail?.platformRequirementIssues ?? [],
            contractDocumentCount: validation.contractDocumentCount,
          };
        } catch (error) {
          // Don't let one failed tab block the whole item (a rejected
          // `Promise.all` would drop every tab's detail and wedge
          // `isResubmitReady` false). Log the real error and degrade this tab
          // to "no suggestions to ignore" so the rest still load and the agent
          // can still resubmit the item.
          // eslint-disable-next-line no-console
          console.error(
            `Failed to load checklist item document ${doc.ingestionDocumentId} for resubmit`,
            error
          );

          return {
            ingestionDocumentId: doc.ingestionDocumentId,
            documentName: doc.documentName,
            ingestionDocumentValidationId: null,
            suggestions: [],
            platformRequirementIssues: [],
            contractDocumentCount: 0,
          };
        }
      })
    )
      .then((results) => {
        if (!cancelled) {
          setDocDetails(results);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, token, ingestionFolderUuid, docIdsKey, refreshKey]);

  return { docDetails, loading };
};
